import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Coins } from 'lucide-react';

interface StoryTokens {
  input: number;
  output: number;
  cacheRead: number;
  total: number;
}

interface Props {
  totalInput: number;
  totalOutput: number;
  totalCacheRead: number;
  total: number;
  sessionCount: number;
  byStory: Record<string, StoryTokens>;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function BreakdownBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.max((value / max) * 100, 0.5) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-neutral-500 w-14 text-right shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-neutral-600 w-12 text-right shrink-0 tabular-nums">
        {formatTokens(value)}
      </span>
    </div>
  );
}

export function TokenUsageCard({ totalInput, totalOutput, totalCacheRead, total, sessionCount, byStory }: Props) {
  const [expanded, setExpanded] = useState(false);

  const sortedStories = useMemo(() => {
    return Object.entries(byStory)
      .sort(([, a], [, b]) => b.total - a.total);
  }, [byStory]);

  const maxTotal = useMemo(() => {
    return Math.max(totalInput, totalOutput, totalCacheRead, 1);
  }, [totalInput, totalOutput, totalCacheRead]);

  const maxStoryTotal = useMemo(() => {
    if (sortedStories.length === 0) return 1;
    return Math.max(...sortedStories.map(([, s]) => s.total), 1);
  }, [sortedStories]);

  if (total === 0) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 text-center">
        <Coins size={20} className="mx-auto text-neutral-300 mb-2" />
        <p className="text-[11px] text-neutral-400">No token usage data yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center justify-between">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Token Usage</p>
        <span className="text-[10px] text-neutral-400">
          {sessionCount} session{sessionCount !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Headline total */}
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-neutral-800 tabular-nums">{formatTokens(total)}</span>
          <span className="text-[11px] text-neutral-400">tokens total</span>
        </div>

        {/* Breakdown bars */}
        <div className="space-y-1.5">
          <BreakdownBar label="Input" value={totalInput} max={maxTotal} color="bg-blue-500" />
          <BreakdownBar label="Output" value={totalOutput} max={maxTotal} color="bg-coral-500" />
          <BreakdownBar label="Cache" value={totalCacheRead} max={maxTotal} color="bg-amber-400" />
        </div>
      </div>

      {/* Per-story breakdown */}
      {sortedStories.length > 0 && (
        <div className="border-t border-neutral-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-neutral-50 transition-colors"
          >
            {expanded
              ? <ChevronDown size={12} className="text-neutral-400" />
              : <ChevronRight size={12} className="text-neutral-400" />
            }
            <span className="text-[10px] font-semibold text-neutral-500">
              By Story ({sortedStories.length})
            </span>
          </button>

          {expanded && (
            <div className="px-4 pb-3 space-y-2">
              {sortedStories.map(([storyId, tokens]) => (
                <div key={storyId}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-mono font-semibold text-neutral-600">{storyId}</span>
                    <span className="text-[10px] font-mono text-neutral-500 tabular-nums">{formatTokens(tokens.total)}</span>
                  </div>
                  <div className="flex h-1.5 rounded-full overflow-hidden bg-neutral-100">
                    <div
                      className="bg-blue-500 h-full"
                      style={{ width: `${(tokens.input / maxStoryTotal) * 100}%` }}
                      title={`Input: ${formatTokens(tokens.input)}`}
                    />
                    <div
                      className="bg-coral-500 h-full"
                      style={{ width: `${(tokens.output / maxStoryTotal) * 100}%` }}
                      title={`Output: ${formatTokens(tokens.output)}`}
                    />
                    <div
                      className="bg-amber-400 h-full"
                      style={{ width: `${(tokens.cacheRead / maxStoryTotal) * 100}%` }}
                      title={`Cache: ${formatTokens(tokens.cacheRead)}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
